import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  MagnifyingGlassIcon,
  FunnelIcon,
  ChevronDownIcon,
  NewspaperIcon,
  CameraIcon,
  MicrophoneIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ArrowDownTrayIcon,
  EyeIcon,
  EyeSlashIcon,
  CheckIcon,
  XMarkIcon,
  ClipboardDocumentListIcon,
} from '@heroicons/react/24/outline';
import { supabase } from '../../lib/supabase';
import { toast } from 'react-hot-toast';
import DeleteConfirmationModal from '../../components/DeleteConfirmationModal';
import PressRegistrationDetailsModal from './PressRegistrationDetailsModal';
import PressCoverageManager from './PressCoverageManager';
import CountUp from 'react-countup';

interface PressRegistration {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  media_outlet: string;
  position: string;
  media_type: string;
  other_media_type?: string;
  team_size: string;
  equipment_details?: string;
  coverage_plans: string;
  arrival_date: string;
  special_requests?: string;
  registration_number: string;
  status: string;
  created_at: string;
  updated_at: string;
  tournament_id?: string;
  tournament_name?: string;
}

interface FormattedPressRegistration {
  id: string;
  name: string;
  email: string;
  phone: string;
  mediaOutlet: string;
  position: string;
  mediaType: string;
  teamSize: string;
  registrationDate: string;
  status: string;
  registrationNumber: string;
  tournamentName?: string;
  fullDetails: PressRegistration;
}

interface PressStats {
  total: number;
  pending: number;
  approved: number;
  rejected: number;
  unique_outlets: number;
  by_type: {
    print: number;
    digital: number;
    tv: number;
    radio: number;
    photography: number;
  };
}

export default function PressManagement() {
  const [activeTab, setActiveTab] = useState<'registrations' | 'coverage'>('registrations');
  const [registrations, setRegistrations] = useState<PressRegistration[]>([]);
  const [filteredRegistrations, setFilteredRegistrations] = useState<FormattedPressRegistration[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedMediaType, setSelectedMediaType] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedRegistration, setSelectedRegistration] = useState<FormattedPressRegistration | null>(null);
  const [stats, setStats] = useState<PressStats | null>(null);
  const [error, setError] = useState('');
  const [isExporting, setIsExporting] = useState(false);
  const [pressPublished, setPressPublished] = useState(true);
  const [updatingPress, setUpdatingPress] = useState(false);

  const recordsPerPageOptions = [5, 10, 25, 50];

  useEffect(() => {
    fetchRegistrations();
    fetchStats();
    fetchPressStatus();
  }, []);

  useEffect(() => {
    filterRegistrations();
  }, [registrations, searchTerm, selectedStatus, selectedMediaType]);

  const fetchRegistrations = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('press_registration_view')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      if (data) {
        setRegistrations(data);
      }
    } catch (err) {
      console.error('Error fetching press registrations:', err);
      setError('Failed to load press registrations');
      toast.error('Failed to load press registrations');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      // Call the function without any parameters to use the no-argument version
      const { data, error } = await supabase
        .rpc('get_press_registration_stats')
        .maybeSingle();

      if (error) throw error;

      if (data) {
        setStats(data);
      }
    } catch (err) {
      console.error('Error fetching press stats:', err);
    }
  };

  const fetchPressStatus = async () => {
    try {
      const { data, error } = await supabase
        .from('system_settings')
        .select('value')
        .eq('key', 'press_published')
        .maybeSingle();

      if (!error && data) {
        setPressPublished(data.value === true || data.value === 'true');
      } else {
        setPressPublished(true);
      }
    } catch (err) {
      console.error('Error fetching press status:', err);
    }
  };

  const togglePressVisibility = async () => {
    try {
      setUpdatingPress(true);

      const { data, error: checkError } = await supabase
        .from('system_settings')
        .select('id')
        .eq('key', 'press_published')
        .maybeSingle();

      if (checkError && !checkError.message.includes('No rows found')) {
        throw checkError;
      }

      const newStatus = !pressPublished;

      if (data) {
        const { error: updateError } = await supabase
          .from('system_settings')
          .update({
            value: newStatus,
            updated_at: new Date().toISOString()
          })
          .eq('id', data.id);

        if (updateError) throw updateError;
      } else {
        const { error: insertError } = await supabase
          .from('system_settings')
          .insert([{
            key: 'press_published',
            value: newStatus,
            category: 'content',
            description: 'Controls whether the Press Page is publicly visible',
            is_public: true
          }]);

        if (insertError) throw insertError;
      }

      setPressPublished(newStatus);
      toast.success(`Press Page ${newStatus ? 'published' : 'unpublished'} successfully`);
    } catch (err) {
      console.error('Error toggling press visibility:', err);
      toast.error('Failed to update press visibility');
    } finally {
      setUpdatingPress(false);
    }
  };

  const formatRegistrations = (regs: PressRegistration[]): FormattedPressRegistration[] => {
    return regs.map(reg => ({
      id: reg.id,
      name: `${reg.first_name} ${reg.last_name}`,
      email: reg.email,
      phone: reg.phone,
      mediaOutlet: reg.media_outlet,
      position: reg.position,
      mediaType: (() => {
        let types: string[] = [];
        try {
          const parsed = JSON.parse(reg.media_type);
          types = Array.isArray(parsed) ? parsed : [reg.media_type];
        } catch {
          types = [reg.media_type];
        }
        return types
          .map(t => t === 'Other' && reg.other_media_type ? reg.other_media_type : t)
          .join(', ');
      })(),
      teamSize: reg.team_size,
      registrationDate: new Date(reg.created_at).toLocaleDateString(),
      status: reg.status,
      registrationNumber: reg.registration_number,
      tournamentName: reg.tournament_name,
      fullDetails: reg
    }));
  };

  const filterRegistrations = () => {
    const formatted = formatRegistrations(registrations);
    
    let filtered = [...formatted];
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        reg => 
          reg.name.toLowerCase().includes(term) ||
          reg.email.toLowerCase().includes(term) ||
          reg.mediaOutlet.toLowerCase().includes(term) ||
          reg.registrationNumber.toLowerCase().includes(term)
      );
    }
    
    if (selectedStatus !== 'all') {
      filtered = filtered.filter(reg => reg.status === selectedStatus);
    }
    
    if (selectedMediaType !== 'all') {
      filtered = filtered.filter(reg => reg.mediaType === selectedMediaType);
    }
    
    setFilteredRegistrations(filtered);
    setCurrentPage(1);
  };

  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = filteredRegistrations.slice(indexOfFirstRecord, indexOfLastRecord);
  const totalPages = Math.ceil(filteredRegistrations.length / recordsPerPage);

  const paginate = (pageNumber: number) => setCurrentPage(pageNumber);

  const handleRecordsPerPageChange = (value: number) => {
    setRecordsPerPage(value);
    setCurrentPage(1);
  };

  const handleViewDetails = (registration: FormattedPressRegistration) => {
    setSelectedRegistration(registration);
    setIsModalOpen(true);
  };

  const handleDeleteRegistration = (registration: FormattedPressRegistration) => {
    setSelectedRegistration(registration);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!selectedRegistration) return;

    try {
      const { error } = await supabase
        .from('press_registrations')
        .delete()
        .eq('id', selectedRegistration.id);

      if (error) throw error;

      await fetchRegistrations();
      await fetchStats();
      toast.success('Registration deleted successfully');
    } catch (err) {
      console.error('Error deleting registration:', err);
      toast.error('Failed to delete registration');
    } finally {
      setIsDeleteModalOpen(false);
      setSelectedRegistration(null);
    }
  };

  const updateRegistrationStatus = async (id: string, status: string) => {
    try {
      const { error } = await supabase
        .from('press_registrations')
        .update({ status, updated_at: new Date().toISOString() })
        .eq('id', id);

      if (error) throw error;

      await fetchRegistrations();
      await fetchStats();
      toast.success(`Registration ${status} successfully`);
    } catch (err) {
      console.error('Error updating registration status:', err);
      toast.error('Failed to update registration status');
    }
  };

  const exportRegistrations = () => {
    try {
      setIsExporting(true);
      
      const headers = ['Name', 'Email', 'Phone', 'Media Outlet', 'Position', 'Media Type', 'Team Size', 'Registration Date', 'Status', 'Registration Number'];
      const csvContent = [
        headers.join(','),
        ...filteredRegistrations.map(reg => [
          `"${reg.name}"`,
          `"${reg.email}"`,
          `"${reg.phone}"`,
          `"${reg.mediaOutlet}"`,
          `"${reg.position}"`,
          `"${reg.mediaType}"`,
          `"${reg.teamSize}"`,
          `"${reg.registrationDate}"`,
          `"${reg.status}"`,
          `"${reg.registrationNumber}"`
        ].join(','))
      ].join('\n');
      
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', 'press_registrations.csv');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success('Press registrations exported successfully');
    } catch (err) {
      console.error('Error exporting registrations:', err);
      toast.error('Failed to export registrations');
    } finally {
      setIsExporting(false);
    }
  };

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return pageNumbers;
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-golf-gray">Press Management</h2>
        {activeTab === 'registrations' && (
          <button
            onClick={exportRegistrations}
            disabled={isExporting || filteredRegistrations.length === 0}
            className="px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors inline-flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ArrowDownTrayIcon className="w-5 h-5" />
            <span>{isExporting ? 'Exporting...' : 'Export Registrations'}</span>
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-gray-100 p-1 rounded-xl w-fit">
        <button
          onClick={() => setActiveTab('registrations')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            activeTab === 'registrations'
              ? 'bg-white text-golf-gray shadow-sm'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <ClipboardDocumentListIcon className="w-4 h-4" />
          Registrations
        </button>
        <button
          onClick={() => setActiveTab('coverage')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            activeTab === 'coverage'
              ? 'bg-white text-golf-gray shadow-sm'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <NewspaperIcon className="w-4 h-4" />
          Press Coverage
        </button>
      </div>

      {activeTab === 'coverage' && <PressCoverageManager />}

      {activeTab === 'registrations' && <>

      {/* Press Page Visibility Control */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 px-3 sm:px-4 py-2 mb-6">
        <div className="flex items-center justify-between gap-2 sm:gap-4">
          <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
            <div className={`p-1 rounded ${pressPublished ? 'bg-green-100' : 'bg-yellow-100'}`}>
              {pressPublished ? (
                <EyeIcon className="w-4 h-4 text-green-600" />
              ) : (
                <EyeSlashIcon className="w-4 h-4 text-yellow-600" />
              )}
            </div>
            <span className="text-xs sm:text-sm font-medium text-golf-gray">
              <span className="hidden sm:inline">Press Page Visibility:</span>
              <span className="sm:hidden">Press:</span>
            </span>
            <span className="hidden md:inline text-sm text-gray-600">
              {pressPublished ? 'Visible to all' : 'Hidden from public'}
            </span>
          </div>
          <button
            onClick={togglePressVisibility}
            disabled={updatingPress}
            className={`px-2 sm:px-3 py-1.5 rounded-lg flex items-center gap-1 sm:gap-1.5 text-xs font-medium transition-colors whitespace-nowrap flex-shrink-0 ${
              pressPublished
                ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
                : 'bg-green-100 text-green-800 hover:bg-green-200'
            } disabled:opacity-50`}
          >
            {updatingPress ? (
              <span className="hidden sm:inline">Updating...</span>
            ) : pressPublished ? (
              <>
                <EyeSlashIcon className="w-3.5 h-3.5" />
                <span>Hide</span>
              </>
            ) : (
              <>
                <EyeIcon className="w-3.5 h-3.5" />
                <span>Show</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-xl shadow-sm p-4 sm:p-6 border border-gray-200"
        >
          <div className="flex items-center space-x-2 sm:space-x-4">
            <div className="bg-blue-500/10 p-2 sm:p-3 rounded-xl">
              <NewspaperIcon className="w-6 h-6 text-blue-500" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Registrations</p>
              <CountUp
                end={stats?.total || 0}
                duration={2}
                className="text-2xl font-bold text-golf-gray"
              />
            </div>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-4">
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="text-sm text-gray-500">Pending</div>
              <div className="text-lg font-semibold text-golf-gray">{stats?.pending || 0}</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="text-sm text-gray-500">Approved</div>
              <div className="text-lg font-semibold text-golf-gray">{stats?.approved || 0}</div>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="bg-white rounded-xl shadow-sm p-4 sm:p-6 border border-gray-200"
        >
          <div className="flex items-center space-x-2 sm:space-x-4">
            <div className="bg-green-500/10 p-2 sm:p-3 rounded-xl">
              <CameraIcon className="w-6 h-6 text-green-500" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Media Outlets</p>
              <CountUp
                end={stats?.unique_outlets || 0}
                duration={2}
                className="text-2xl font-bold text-golf-gray"
              />
            </div>
          </div>
          <div className="mt-4">
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="text-sm text-gray-500">Media Coverage</div>
              <div className="mt-2 space-y-2">
                <div className="flex justify-between items-center text-xs">
                  <span>Print</span>
                  <span>{stats?.by_type.print || 0}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-1.5">
                  <div 
                    className="bg-blue-500 rounded-full h-1.5" 
                    style={{ width: `${stats?.total ? (stats.by_type.print / stats.total) * 100 : 0}%` }}
                  />
                </div>
                <div className="flex justify-between items-center text-xs">
                  <span>Digital</span>
                  <span>{stats?.by_type.digital || 0}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-1.5">
                  <div 
                    className="bg-green-500 rounded-full h-1.5" 
                    style={{ width: `${stats?.total ? (stats.by_type.digital / stats.total) * 100 : 0}%` }}
                  />
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="bg-white rounded-xl shadow-sm p-4 sm:p-6 border border-gray-200"
        >
          <div className="flex items-center space-x-2 sm:space-x-4">
            <div className="bg-teal-500/10 p-2 sm:p-3 rounded-xl">
              <MicrophoneIcon className="w-6 h-6 text-teal-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Broadcast Media</p>
              <CountUp
                end={(stats?.by_type.tv || 0) + (stats?.by_type.radio || 0)}
                duration={2}
                className="text-2xl font-bold text-golf-gray"
              />
            </div>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-4">
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="text-sm text-gray-500">TV</div>
              <div className="text-lg font-semibold text-golf-gray">{stats?.by_type.tv || 0}</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="text-sm text-gray-500">Radio</div>
              <div className="text-lg font-semibold text-golf-gray">{stats?.by_type.radio || 0}</div>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="bg-white rounded-xl shadow-sm p-4 sm:p-6 border border-gray-200"
        >
          <div className="flex items-center space-x-2 sm:space-x-4">
            <div className="bg-orange-500/10 p-2 sm:p-3 rounded-xl">
              <CameraIcon className="w-6 h-6 text-orange-500" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Photography</p>
              <CountUp
                end={stats?.by_type.photography || 0}
                duration={2}
                className="text-2xl font-bold text-golf-gray"
              />
            </div>
          </div>
          <div className="mt-4">
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="text-sm text-gray-500">Status Breakdown</div>
              <div className="mt-2 space-y-2">
                <div className="flex justify-between items-center text-xs">
                  <span className="flex items-center">
                    <span className="w-2 h-2 bg-yellow-400 rounded-full mr-1"></span>
                    Pending
                  </span>
                  <span>{stats?.pending || 0}</span>
                </div>
                <div className="flex justify-between items-center text-xs">
                  <span className="flex items-center">
                    <span className="w-2 h-2 bg-green-500 rounded-full mr-1"></span>
                    Approved
                  </span>
                  <span>{stats?.approved || 0}</span>
                </div>
                <div className="flex justify-between items-center text-xs">
                  <span className="flex items-center">
                    <span className="w-2 h-2 bg-red-500 rounded-full mr-1"></span>
                    Rejected
                  </span>
                  <span>{stats?.rejected || 0}</span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-3 sm:p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name, email, outlet, or registration number..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-golf-orange focus:border-golf-orange"
              />
            </div>
          </div>

          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <FunnelIcon className="w-5 h-5 text-gray-500" />
            <span className="text-gray-700">Filters</span>
            <ChevronDownIcon className={`w-4 h-4 text-gray-500 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
          </button>
        </div>

        {showFilters && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4 pt-4 border-t border-gray-200"
          >
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="w-full border border-gray-300 rounded-lg focus:ring-golf-orange focus:border-golf-orange"
              >
                <option value="all">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Media Type</label>
              <select
                value={selectedMediaType}
                onChange={(e) => setSelectedMediaType(e.target.value)}
                className="w-full border border-gray-300 rounded-lg focus:ring-golf-orange focus:border-golf-orange"
              >
                <option value="all">All Media Types</option>
                <option value="Print">Print</option>
                <option value="Digital">Digital</option>
                <option value="TV">TV</option>
                <option value="Radio">Radio</option>
                <option value="Photography">Photography</option>
              </select>
            </div>
          </motion.div>
        )}
      </div>

      {/* Press Registrations Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-3 sm:px-6 py-3 border-b border-gray-200 bg-gray-50 flex flex-col sm:flex-row justify-between items-center gap-2 sm:gap-0">
          <div className="flex items-center space-x-2 w-full sm:w-auto">
            <span className="text-sm text-gray-500">Show</span>
            <select
              value={recordsPerPage}
              onChange={(e) => handleRecordsPerPageChange(Number(e.target.value))}
              className="border border-gray-300 rounded-lg text-sm focus:ring-golf-orange focus:border-golf-orange"
            >
              {recordsPerPageOptions.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
            <span className="text-sm text-gray-500">entries</span>
          </div>
          <div className="text-sm text-gray-500 w-full sm:w-auto text-center sm:text-right">
            {loading ? (
              'Loading registrations...'
            ) : (
              `Showing ${filteredRegistrations.length > 0 ? indexOfFirstRecord + 1 : 0} to ${Math.min(indexOfLastRecord, filteredRegistrations.length)} of ${filteredRegistrations.length} entries`
            )}
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-golf-orange border-t-transparent"></div>
          </div>
        ) : error ? (
          <div className="text-center py-12 text-red-600">
            <p>{error}</p>
            <button
              onClick={fetchRegistrations}
              className="mt-4 text-golf-orange hover:text-golf-green"
            >
              Try Again
            </button>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">Media Outlet</th>
                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">Media Type</th>
                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden lg:table-cell">Registration Date</th>
                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-3 sm:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {currentRecords.length > 0 ? (
                    currentRecords.map((registration) => (
                      <tr key={registration.id} className="hover:bg-gray-50">
                        <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10">
                              <div className="h-10 w-10 rounded-full bg-golf-orange/10 flex items-center justify-center">
                                <span className="text-golf-orange font-medium">
                                  {registration.name.split(' ').map(n => n[0]).join('')}
                                </span>
                              </div>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">{registration.name}</div>
                              <div className="text-sm text-gray-500">{registration.email}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-3 sm:px-6 py-4 whitespace-nowrap hidden md:table-cell">
                          <div className="text-sm text-gray-900">{registration.mediaOutlet}</div>
                          <div className="text-sm text-gray-500">{registration.position}</div>
                        </td>
                        <td className="px-3 sm:px-6 py-4 whitespace-nowrap hidden sm:table-cell">
                          <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            registration.mediaType === 'Print' ? 'bg-blue-100 text-blue-800' :
                            registration.mediaType === 'Digital' ? 'bg-green-100 text-green-800' :
                            registration.mediaType === 'TV' ? 'bg-teal-100 text-teal-800' :
                            registration.mediaType === 'Radio' ? 'bg-yellow-100 text-yellow-800' :
                            registration.mediaType === 'Photography' ? 'bg-pink-100 text-pink-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {registration.mediaType}
                          </span>
                          <div className="text-xs text-gray-500 mt-1 hidden md:block">Team: {registration.teamSize}</div>
                        </td>
                        <td className="px-3 sm:px-6 py-4 whitespace-nowrap hidden lg:table-cell">
                          <div className="text-sm text-gray-900">{registration.registrationDate}</div>
                          <div className="text-xs text-gray-500">{registration.registrationNumber}</div>
                        </td>
                        <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            registration.status === 'approved' ? 'bg-green-100 text-green-800' :
                            registration.status === 'rejected' ? 'bg-red-100 text-red-800' :
                            'bg-yellow-100 text-yellow-800'
                          }`}>
                            {registration.status.charAt(0).toUpperCase() + registration.status.slice(1)}
                          </span>
                        </td>
                        <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex justify-end space-x-2">
                            <button
                              onClick={() => handleViewDetails(registration)}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                              title="View Details"
                            >
                              <EyeIcon className="w-5 h-5" />
                            </button>
                            {registration.status === 'pending' && (
                              <>
                                <button
                                  onClick={() => updateRegistrationStatus(registration.id, 'approved')}
                                  className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                                  title="Approve"
                                >
                                  <CheckIcon className="w-5 h-5" />
                                </button>
                                <button
                                  onClick={() => updateRegistrationStatus(registration.id, 'rejected')}
                                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                  title="Reject"
                                >
                                  <XMarkIcon className="w-5 h-5" />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={6} className="px-3 sm:px-6 py-4 text-center text-gray-500">
                        No registrations found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {filteredRegistrations.length > 0 && (
              <div className="px-3 sm:px-6 py-3 border-t border-gray-200 bg-gray-50 flex flex-col sm:flex-row justify-between items-center gap-4">
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => paginate(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeftIcon className="w-5 h-5 text-gray-500" />
                  </button>
                  {getPageNumbers().map((number) => (
                    <button
                      key={number}
                      onClick={() => paginate(number)}
                      className={`px-3 py-1 rounded-lg ${
                        currentPage === number
                          ? 'bg-golf-orange text-white'
                          : 'border border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {number}
                    </button>
                  ))}
                  <button
                    onClick={() => paginate(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRightIcon className="w-5 h-5 text-gray-500" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
      </>}

      {/* Modals */}
      {isModalOpen && selectedRegistration && (
        <PressRegistrationDetailsModal
          registration={selectedRegistration.fullDetails}
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setSelectedRegistration(null);
          }}
        />
      )}

      {isDeleteModalOpen && selectedRegistration && (
        <DeleteConfirmationModal
          isOpen={isDeleteModalOpen}
          onClose={() => {
            setIsDeleteModalOpen(false);
            setSelectedRegistration(null);
          }}
          onConfirm={confirmDelete}
          title="Delete Press Registration"
          message={`Are you sure you want to delete the press registration for ${selectedRegistration.name}? This action cannot be undone.`}
        />
      )}
    </div>
  );
}